import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import PrivateRoute from './shared/components/PrivateRoute';
import SessionTimeout from './shared/components/SessionTimeout';
import MainLayout from './shared/layout/MainLayout';
import ErrorBoundary from './shared/components/ErrorBoundary';
import {
  AlertsErrorBoundary,
  IncidentsErrorBoundary,
  DashboardErrorBoundary,
} from './shared/components/FeatureErrorBoundary';
import { ThemeProvider } from './shared/context/ThemeContext';

import SsoProvider from './shared/auth/SsoProvider';
import LoginPage from './features/auth/LoginPage';
import CallbackPage from './features/auth/CallbackPage';
import SessionExpiredPage from './features/auth/SessionExpiredPage';
import DashboardPage from './features/dashboard/DashboardPage';
import DashboardPageV2 from './features/dashboard/DashboardPageV2';
import DashboardDrilldownPage from './features/dashboard/DashboardDrilldownPage';
import PresentationPage from './features/dashboard/PresentationPage';
import BusinessTransactionsPage from './features/business-transactions/BusinessTransactionsPage';
import BusinessTransactionDetailPage from './features/business-transactions/BusinessTransactionDetailPage';
import AlertsPage from './features/alerts/AlertsPage';
import AlertDetailPage from './features/alerts/AlertDetailPage';
import IncidentsPage from './features/incidents/IncidentsPage';
import IncidentDetailPage from './features/incidents/IncidentDetailPage';
import CreateIncidentPage from './features/incidents/CreateIncidentPage';
import PostIncidentReviewsPage from './features/incidents/PostIncidentReviewsPage';
import GithubPrsPage from './features/github-prs/GithubPrsPage';
import MainDashboardPage from './features/dashboard/MainDashboardPage';
import FinanceDashboardPage from './features/finance-metering/FinanceDashboardPage';
import FinanceRecordsPage from './features/finance-metering/FinanceRecordsPage';

import './shared/styles/global.css';

/**
 * Root component — defines all app routes.
 * Protected routes require a valid JWT token (enforced by PrivateRoute).
 * SessionTimeout monitors token expiry across all protected pages.
 *
 * SsoProvider wraps the Router rather than sitting inside it: it provides no
 * routes of its own, and CallbackPage needs both it and useNavigate.
 *
 * Structure mirrors ecoh-ui-module's App.js, our reference for UI patterns:
 *
 *   ErrorBoundary  →  nothing can blank the screen without saying why
 *     Routes
 *       public routes
 *       PrivateRoute + MainLayout  →  guard and chrome declared once
 *         feature boundary + page   →  a crash costs one feature, not the app
 *
 * The layout route replaces what used to be <SessionTimeout /> and <Navbar />
 * hand-copied into all sixteen protected routes.
 *
 * Note for whoever needs them next: PrEventsPage, PrEventDetailPage and
 * FileUploadPage were imported here but had no routes, so they were unreachable
 * and only added weight to the bundle. The imports are gone; the features are
 * untouched under src/features/ and need a <Route> to come back.
 */
function App() {
  return (
    <ThemeProvider>
    <SsoProvider>
    <Router>
      {/* Last line of defence: a render error anywhere shows a message with a
          retry rather than the empty page that React leaves behind by default. */}
      <ErrorBoundary componentName="Application">
      <Routes>

        {/* Public routes.
            /callback MUST stay outside PrivateRoute. The user arriving back from
            Microsoft has no session yet — that is the entire point of the
            request — so a route guard here would redirect them to /login and
            discard the authorization code before it could be exchanged. */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/callback" element={<CallbackPage />} />
        <Route path="/session-expired" element={<SessionExpiredPage />} />

        {/* Protected routes — require a JWT token, and share one shell.
            Each feature carries its own boundary so a crash in Alerts leaves the
            sidebar and every other feature usable. */}
        <Route element={<PrivateRoute><MainLayout /></PrivateRoute>}>

          <Route
            path="/dashboard"
            element={<DashboardErrorBoundary><MainDashboardPage /></DashboardErrorBoundary>}
          />
          <Route
            path="/dashboard/drilldown"
            element={<DashboardErrorBoundary><DashboardDrilldownPage /></DashboardErrorBoundary>}
          />
          <Route
            path="/dashboard-classic"
            element={<DashboardErrorBoundary><DashboardPage /></DashboardErrorBoundary>}
          />
          <Route
            path="/dashboard-v2"
            element={<DashboardErrorBoundary><DashboardPageV2 /></DashboardErrorBoundary>}
          />

          <Route path="/business-transactions" element={<BusinessTransactionsPage />} />
          <Route path="/business-transactions/:id" element={<BusinessTransactionDetailPage />} />

          <Route
            path="/alerts"
            element={<AlertsErrorBoundary><AlertsPage /></AlertsErrorBoundary>}
          />
          <Route
            path="/alerts/:alertId"
            element={<AlertsErrorBoundary><AlertDetailPage /></AlertsErrorBoundary>}
          />

          <Route
            path="/incidents"
            element={<IncidentsErrorBoundary><IncidentsPage /></IncidentsErrorBoundary>}
          />
          <Route
            path="/incidents/create/from-alert/:alertId"
            element={<IncidentsErrorBoundary><CreateIncidentPage /></IncidentsErrorBoundary>}
          />
          <Route
            path="/incidents/:service/:incidentId"
            element={<IncidentsErrorBoundary><IncidentDetailPage /></IncidentsErrorBoundary>}
          />
          <Route
            path="/post-incident-reviews"
            element={<IncidentsErrorBoundary><PostIncidentReviewsPage /></IncidentsErrorBoundary>}
          />

          <Route path="/github-prs" element={<GithubPrsPage />} />

          <Route path="/finance/dashboard" element={<FinanceDashboardPage />} />
          <Route path="/finance/records" element={<FinanceRecordsPage />} />

        </Route>

        {/* Protected but deliberately outside MainLayout: the presentation view
            is a wallboard, and the sidebar would only take up screen. */}
        <Route
          path="/presentation"
          element={
            <PrivateRoute>
              <SessionTimeout />
              <PresentationPage />
            </PrivateRoute>
          }
        />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />

      </Routes>
      </ErrorBoundary>
    </Router>
    </SsoProvider>
    </ThemeProvider>
  );
}

export default App;
